import React, { useMemo, useState, useCallback } from "react";
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    createColumnHelper,
    flexRender,
    SortingState,
    ColumnDef,
} from "@tanstack/react-table";
import {
    Paper,
    Typography,
    Box,
    ToggleButton,
    ToggleButtonGroup,
    Skeleton,
    Alert,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
} from "@mui/material";
import {
    ViewList as ViewListIcon,
    Visibility as VisibilityIcon,
    Schedule as ScheduleIcon,
    ArrowUpward as ArrowUpwardIcon,
    ArrowDownward as ArrowDownwardIcon,
} from "@mui/icons-material";
import { TrackedObject, TrackedObjectWithOrdinal } from "../../../types/TrackedObject";
import { directionTypeKeyToDirection } from "../../../types/Direction";

interface DisplayObject extends TrackedObjectWithOrdinal {
    duration: string;
}

export interface VideoObjectsTableProps {
    objects: TrackedObject[];
    currentObjectIds?: Set<string>;
    isLoading?: boolean;
    videoStartTime: Date;
    fetchError?: Error | null;
    hideDirections: boolean;
    onSeekToObject?: (timestamp: string) => void;
    onObjectHover?: (objectId: string | null) => void;
    mode?: "all" | "current";
    onModeChange?: (mode: "all" | "current") => void;
}

const columnHelper = createColumnHelper<DisplayObject>();

const VideoObjectsTable: React.FC<VideoObjectsTableProps> = ({
    objects = [],
    currentObjectIds = new Set<string>(),
    isLoading = true,
    videoStartTime,
    fetchError,
    hideDirections,
    onSeekToObject,
    mode = "all",
    onModeChange,
}) => {
    const [sorting, setSorting] = useState<SortingState>([{ id: "ordinal", desc: false }]);

    // Helper to convert absolute time to video-relative time
    const absoluteTimeToVideo = useCallback(
        (absoluteTime: string): number => {
            const objectTime = new Date(absoluteTime).getTime();
            const videoTime = videoStartTime.getTime();
            return Math.max(0, (objectTime - videoTime) / 1000);
        },
        [videoStartTime],
    );

    // Format duration
    const formatDuration = useCallback((firstSeen: string, lastSeen: string): string => {
        const start = new Date(firstSeen).getTime();
        const end = new Date(lastSeen).getTime();
        const duration = (end - start) / 1000;

        if (duration < 60) {
            return `${duration.toFixed(1)}s`;
        } else if (duration < 3600) {
            const minutes = Math.floor(duration / 60);
            const seconds = Math.floor(duration % 60);
            return `${minutes}m ${seconds}s`;
        } else {
            const hours = Math.floor(duration / 3600);
            const minutes = Math.floor((duration % 3600) / 60);
            return `${hours}h ${minutes}m`;
        }
    }, []);

    // Create objects with ordinal numbers based on firstSeen timestamp
    const objectsWithOrdinal = useMemo((): DisplayObject[] => {
        // First, sort all objects by firstSeen timestamp
        const sortedByTime = [...objects].sort(
            (a, b) => new Date(a.firstSeen).getTime() - new Date(b.firstSeen).getTime(),
        );

        // Add ordinal numbers (1-based index)
        return sortedByTime.map((obj, index) => ({
            ...obj,
            duration: formatDuration(obj.firstSeen, obj.lastSeen),
            ordinal: index + 1,
        }));
    }, [objects]);

    // Derive currentObjects from objects and currentObjectIds
    const currentObjects = useMemo(() => {
        return objects.filter((obj) => currentObjectIds.has(obj.id));
    }, [objects, currentObjectIds]);

    // Get display data based on mode
    const displayData = useMemo((): DisplayObject[] => {
        if (mode === "current") {
            // For current objects, find their ordinal from the full list
            return currentObjects.map((currentObj) => {
                const objectWithOrdinal = objectsWithOrdinal.find((o) => o.id === currentObj.id);
                return {
                    ...currentObj,
                    ordinal: objectWithOrdinal?.ordinal || 0,
                    duration: objectWithOrdinal?.duration || "0s",
                };
            });
        }

        return objectsWithOrdinal;
    }, [mode, currentObjects, objectsWithOrdinal]);

    // Format timestamp relative to video
    const formatVideoTime = useCallback(
        (timestamp: string): string => {
            const videoTime = absoluteTimeToVideo(timestamp);
            const minutes = Math.floor(videoTime / 60);
            const seconds = Math.floor(videoTime % 60);
            return `${minutes}:${seconds.toString().padStart(2, "0")}`;
        },
        [absoluteTimeToVideo],
    );

    // Check if object is currently visible
    const isCurrentlyVisible = useCallback(
        (obj: TrackedObject): boolean => currentObjectIds.has(obj.id),
        [currentObjectIds],
    );

    // Handle mode change
    const handleModeChange = (_: React.MouseEvent<HTMLElement>, newMode: "all" | "current" | null) => {
        if (newMode && onModeChange) {
            onModeChange(newMode);
        }
    };

    // Handle seek to object
    const handleSeekToObject = (firstSeenTs: string) => {
        if (onSeekToObject) {
            onSeekToObject(firstSeenTs);
        }
    };

    // Define columns
    const columns = useMemo(
        () =>
            [
                columnHelper.accessor("ordinal", {
                    header: "Object",
                    cell: ({ getValue, row }) => {
                        const isVisible = isCurrentlyVisible(row.original);
                        const obj = row.original;
                        return (
                            <Stack direction="row" spacing={1} alignItems="left">
                                <Chip
                                    label={obj.ordinal}
                                    size="small"
                                    variant={isVisible ? "filled" : "outlined"}
                                    color={isVisible ? "primary" : "default"}
                                    sx={{
                                        fontSize: "0.75rem",
                                    }}
                                />
                            </Stack>
                        );
                    },
                    size: 120,
                }),
                columnHelper.accessor("direction", {
                    header: "Direction",
                    cell: ({ getValue }) => {
                        return (
                            <Typography>
                                {directionTypeKeyToDirection(getValue() as "dir1" | "dir2" | "neutral")}
                            </Typography>
                        );
                    },
                    size: 80,
                }),
                columnHelper.accessor("size.max", {
                    header: "Size",
                    cell: ({ getValue }) => (
                        <Typography variant="body2" fontFamily="monospace">
                            {getValue().toFixed(0)}px
                        </Typography>
                    ),
                    size: 80,
                }),
                columnHelper.accessor("firstSeen", {
                    header: "First Seen",
                    cell: ({ getValue, row }) => {
                        return (
                            <Typography variant="body2" fontFamily="monospace">
                                {formatVideoTime(getValue())}
                            </Typography>
                        );
                    },
                    size: 100,
                }),
                columnHelper.accessor("lastSeen", {
                    header: "Last Seen",
                    cell: ({ getValue, row }) => {
                        return (
                            <Typography variant="body2" fontFamily="monospace">
                                {formatVideoTime(getValue())}
                            </Typography>
                        );
                    },
                    size: 100,
                }),
                columnHelper.accessor("duration", {
                    header: "Duration",
                    cell: ({ getValue, row }) => {
                        return (
                            <Typography variant="body2" fontFamily="monospace">
                                {getValue()}
                            </Typography>
                        );
                    },
                    size: 100,
                }),
            ] as ColumnDef<DisplayObject>[],
        [isCurrentlyVisible, formatVideoTime, formatDuration, handleSeekToObject, isLoading, onSeekToObject],
    );

    // Create table instance
    const table = useReactTable({
        data: displayData,
        columns,
        initialState: {
            columnVisibility: {
                direction: !hideDirections,
            },
        },
        state: {
            sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    if (fetchError) {
        return (
            <Paper elevation={1} sx={{ p: 3 }}>
                <Alert severity="error" icon={<ViewListIcon />}>
                    <Typography variant="subtitle2" gutterBottom>
                        Error loading objects
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {fetchError.message || "Something went wrong while fetching counted objects."}
                    </Typography>
                </Alert>
            </Paper>
        );
    }

    if (isLoading) {
        return (
            <Paper elevation={1} sx={{ p: 2 }}>
                <Box sx={{ mb: 2 }}>
                    <Skeleton variant="rectangular" width={200} height={32} />
                </Box>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {Array.from({ length: 7 }).map((_, index) => (
                                    <TableCell key={index}>
                                        <Skeleton variant="text" width={80} />
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Array.from({ length: 5 }).map((_, rowIndex) => (
                                <TableRow key={rowIndex}>
                                    {Array.from({ length: 7 }).map((_, cellIndex) => (
                                        <TableCell key={cellIndex}>
                                            <Skeleton variant="text" width={80} />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        );
    }

    if (objects.length === 0) {
        return (
            <Paper elevation={1} sx={{ p: 3 }}>
                <Alert severity="info" icon={<ViewListIcon />}>
                    <Typography variant="subtitle2" gutterBottom>
                        No objects found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        No objects were found in this video.
                    </Typography>
                </Alert>
            </Paper>
        );
    }

    return (
        <Paper
            elevation={1}
            sx={{
                overflow: "hidden",
                height: "100%",
                display: "flex",
                flexDirection: "column",
            }}
        >
            {/* Header */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <Box>
                        <Typography variant="h6" component="h3" gutterBottom>
                            Counted Objects
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {mode === "all"
                                ? `${objects.length} total objects`
                                : `${currentObjects.length} currently visible`}
                        </Typography>
                    </Box>

                    {onModeChange && (
                        <ToggleButtonGroup value={mode} exclusive onChange={handleModeChange} size="small">
                            <ToggleButton value="all">
                                <ViewListIcon sx={{ mr: 1, fontSize: 16 }} />
                                All Objects
                            </ToggleButton>
                            <ToggleButton value="current">
                                <VisibilityIcon sx={{ mr: 1, fontSize: 16 }} />
                                Current Only
                            </ToggleButton>
                        </ToggleButtonGroup>
                    )}
                </Stack>
            </Box>

            {/* Table */}
            <TableContainer sx={{ height: "100%" }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableCell
                                        key={header.id}
                                        align={
                                            ["diagonal.min", "diagonal.max", "diagonal.avg"].includes(header.id)
                                                ? "right"
                                                : "left"
                                        }
                                        style={{ width: header.getSize() }}
                                    >
                                        {header.isPlaceholder ? null : (
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    cursor: header.column.getCanSort() ? "pointer" : "default",
                                                    userSelect: "none",
                                                }}
                                                onClick={header.column.getToggleSortingHandler()}
                                            >
                                                <Typography variant="subtitle2" fontWeight={600}>
                                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                                </Typography>
                                                {header.column.getCanSort() && (
                                                    <Box
                                                        sx={{
                                                            ml: 1,
                                                            display: "flex",
                                                            flexDirection: "column",
                                                        }}
                                                    >
                                                        {header.column.getIsSorted() === "asc" && (
                                                            <ArrowUpwardIcon
                                                                sx={{
                                                                    fontSize: 14,
                                                                }}
                                                            />
                                                        )}
                                                        {header.column.getIsSorted() === "desc" && (
                                                            <ArrowDownwardIcon
                                                                sx={{
                                                                    fontSize: 14,
                                                                }}
                                                            />
                                                        )}
                                                        {!header.column.getIsSorted() && (
                                                            <Box
                                                                sx={{
                                                                    opacity: 0.3,
                                                                }}
                                                            >
                                                                <ArrowUpwardIcon
                                                                    sx={{
                                                                        fontSize: 14,
                                                                    }}
                                                                />
                                                            </Box>
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableHead>
                    <TableBody>
                        {table.getRowModel().rows.map((row) => {
                            const isVisible = isCurrentlyVisible(row.original);
                            return (
                                <TableRow
                                    key={row.id}
                                    hover
                                    selected={isVisible}
                                    sx={{
                                        backgroundColor: isVisible ? "action.selected" : undefined,
                                    }}
                                    onClick={() => onSeekToObject && onSeekToObject(row.original.firstSeen)}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell
                                            key={cell.id}
                                            align={cell.column.id === "diagonal.max" ? "right" : "left"}
                                        >
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Footer Stats */}
            {mode === "all" && (
                <Box
                    sx={{
                        p: 2,
                        backgroundColor: "grey.50",
                        borderTop: 1,
                        borderColor: "divider",
                    }}
                >
                    <Stack direction="row" spacing={3}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <ScheduleIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                            <Typography variant="caption">Total Objects: {objects.length}</Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <VisibilityIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                            <Typography variant="caption">Currently Visible: {currentObjects.length}</Typography>
                        </Stack>
                    </Stack>
                </Box>
            )}
        </Paper>
    );
};

export default VideoObjectsTable;
